import { WarningHexagon } from 'iconoir-react'
import { ReactElement, useState, useMemo } from 'react'
import {
  roleValidator,
  ORG_ROLE_SCOPE,
  WORKSPACE_ROLE_SCOPE,
  initializePermissionState,
  extractPermissions,
  filterPermissionGroups,
  getPagePermissionValues,
  permissionDisplayName,
} from './roleFormUtils'
import { Button } from 'app/components/ui/button'
import { DialogActionBar } from 'app/components/ui/dialog'
import { FormInput } from 'app/components/ui/form-input'
import { Label } from 'app/components/ui/label'
import { SmartForm } from 'app/components/ui/smart-form'
import FormComboMultiSelect from 'app/components/form/FormComboMultiSelect'
import FormHiddenInput from 'app/components/form/FormHiddenInput'
import { Option } from 'app/components/selector/SelectAll'
import { SelectAllContent } from 'app/components/selector/SelectAllContent'
import { useListCopy } from 'app/hooks/useListCopy'
import { useRoleFormActions, hasChanges } from 'app/hooks/useRoleFormActions'
import type { Permission, RoleJson } from 'app/types/entities'
import { closeModal } from 'app/lib/modal'
import { notifyError } from 'app/services/notify.client'
import { useUser } from 'app/utils'
import { debug } from 'lib/logging'

type RoleFormProps = {
  role?: RoleJson
  orgId?: string
  workspaceId?: string
  userPermissions?: string[]
  isOrgRole?: boolean
  onSuccess?: () => void
}

const PERMISSION_REQUIRED = `At least one permission is required`

export function RoleForm({ role, orgId, userPermissions, isOrgRole = false, onSuccess }: RoleFormProps): ReactElement {
  const user = useUser()
  const copy = useListCopy()
  const scope = isOrgRole ? ORG_ROLE_SCOPE : WORKSPACE_ROLE_SCOPE
  const isWorkspaceRole = !isOrgRole
  const isOwnerRole = role?.name === `Owner`
  const isNewRole = !role?.id
  const isDefaultRole = role?.isDefault === true
  const hasAllPermissions = !userPermissions && user?.type === `super`

  const { isDuplicate, formInFlight, deleteInFlight, handleDelete, handleSubmit } = useRoleFormActions({
    roleId: role?.id,
    isNewRole,
    onSuccess,
  })

  const availablePagePermissions = useMemo(() => {
    if (hasAllPermissions) return scope.pagePermissions
    return scope.pagePermissions.filter((p) => userPermissions?.includes(p.value))
  }, [hasAllPermissions, userPermissions, scope])

  const displayedPagePermissions = useMemo(
    () => availablePagePermissions.map((p) => ({ ...p, name: permissionDisplayName(p, copy) })),
    [availablePagePermissions, copy],
  )

  const permissionSections = useMemo(
    () =>
      scope.permissionSections(orgId).map((section) => ({
        ...section,
        groups: filterPermissionGroups(section.groups, userPermissions, hasAllPermissions),
      })),
    [hasAllPermissions, userPermissions, orgId, scope],
  )

  const [pagePermissions, setPagePermissions] = useState<string[]>(() => {
    const availableValues = availablePagePermissions.map((p) => p.value)
    const selected = role?.permissions?.filter((p) => availableValues.includes(p)) ?? []
    return selected.length > 0 ? selected : availableValues
  })
  const [sectionOptions, setSectionOptions] = useState<Option[][]>(() =>
    permissionSections.map((section) => initializePermissionState(section.groups, role?.permissions)),
  )
  const [permissionsError, setPermissionsError] = useState(``)
  const [currentName, setCurrentName] = useState(role?.name || ``)

  const allPermissions = useMemo(
    () =>
      [
        ...scope.fixedPagePermissions,
        ...pagePermissions,
        ...sectionOptions.flatMap((options) => extractPermissions(options)),
      ] as Permission[],
    [scope, pagePermissions, sectionOptions],
  )

  const canSave =
    isNewRole ||
    hasChanges({
      newName: currentName,
      newPermissions: allPermissions,
      originalName: role?.name,
      originalPermissions: role?.permissions,
    })
  const pagePermissionValues = useMemo(
    () => getPagePermissionValues(pagePermissions, displayedPagePermissions),
    [pagePermissions, displayedPagePermissions],
  )

  const handleSectionChange = (index: number, options: Option[]) => {
    const next = sectionOptions.map((current, i) => (i === index ? options : current))
    setSectionOptions(next)
    if (!isWorkspaceRole) return
    const hasAnySelected = next.flat().some((group) => group.checked || group.groupOptions?.some((opt) => opt.checked))
    setPermissionsError(hasAnySelected ? `` : PERMISSION_REQUIRED)
  }

  if (!user) return null as unknown as ReactElement

  async function onFormSubmit(data?: { name: string; permissions?: string[] | string }): Promise<void> {
    if (allPermissions.length === 0) {
      setPermissionsError(PERMISSION_REQUIRED)
      notifyError(PERMISSION_REQUIRED)
      return
    }
    await handleSubmit(data, allPermissions)
  }

  return (
    <div className="flex w-[628px] flex-col">
      <div className="flex-1 overflow-hidden pt-4">
        <SmartForm<{ name: string; permissions?: string[] | string }>
          id="role-form"
          key={`role-form-${role?.id || `new`}`}
          validator={roleValidator}
          className="mb-0 mt-0 !flex !flex-col"
          defaultValues={{ name: role?.name || ``, permissions: allPermissions }}
          onSubmit={onFormSubmit}
          preventSubmit
          hideButtons
        >
          <div className="flex h-full shrink flex-col gap-y-6 px-6">
            <FormInput
              name="name"
              label="Role Name"
              disabled={isDefaultRole || isOwnerRole}
              required
              onChange={(e) => setCurrentName(e.target.value)}
            />
            <FormHiddenInput name="permissions" value={allPermissions} />
            <FormComboMultiSelect
              name="pageAccess"
              wrapperClassName="flex flex-col gap-y-0.5"
              labelClassName={`text-default text-heading-xs ${isWorkspaceRole ? `required` : ``}`}
              inputClassName="h-10.5 text-label-sm"
              label="Page Level Access"
              options={displayedPagePermissions}
              values={pagePermissionValues}
              disabled={isOwnerRole}
              deselectAllOptions={[{ name: `All Pages`, id: `0`, value: `` }]}
              onChange={(options) => {
                const selectedValues = options.map((opt) => opt.value).filter((val) => val !== ``)
                const configurablePermissions =
                  selectedValues.length === 0 ? availablePagePermissions.map((p) => p.value) : selectedValues
                setPagePermissions(configurablePermissions)
                debug(`roles`, `Page access changed:`, { options, selectedValues, configurablePermissions })
              }}
              enableDeselectAllOption={isWorkspaceRole}
            />
            {permissionSections.map(
              ({ label, groups }, index) =>
                (isWorkspaceRole || groups.length > 0) && (
                  <div key={label} className={`flex flex-col gap-y-0.5 ${isWorkspaceRole ? `overflow-hidden` : ``}`}>
                    <Label>
                      <span className={isWorkspaceRole ? `required` : undefined}>{label}</span>
                    </Label>
                    <SelectAllContent
                      containerClassName={`flex w-full flex-col border p-3 rounded-lg ${
                        isWorkspaceRole ? `overflow-y-auto h-120 grow` : ``
                      } ${isOwnerRole ? `border-subtle cursor-not-allowed` : `border-default bg-surface`}`}
                      initialOptions={sectionOptions[index]}
                      disabled={isOwnerRole}
                      onChange={(options) => handleSectionChange(index, options)}
                      showSelectAll
                    />
                    {permissionsError && <div className="mt-2 text-body-sm text-red-600">{permissionsError}</div>}
                  </div>
                ),
            )}
          </div>
          <DialogActionBar
            start={
              <>
                {!isNewRole && !isDefaultRole && (
                  <Button type="button" variant="danger" size="md" onClick={handleDelete} disabled={deleteInFlight}>
                    {deleteInFlight ? `Deleting...` : `Delete Role`}
                  </Button>
                )}
                {isDuplicate && (
                  <div className="inline-flex items-center gap-x-2 text-red-700">
                    <WarningHexagon className="size-4" />
                    <span className="text-body-xs">A role with this name already exists</span>
                  </div>
                )}
              </>
            }
          >
            <Button type="button" variant="secondary" size="md" onClick={() => closeModal()}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              type="submit"
              form="role-form"
              disabled={formInFlight || !!permissionsError || !canSave}
            >
              {formInFlight ? `Saving...` : isNewRole ? `Create Role` : `Update Role`}
            </Button>
          </DialogActionBar>
        </SmartForm>
      </div>
    </div>
  )
}
